/**
 * Dependencies.
 */
import { flatten, orderBy } from 'lodash-es';
import {
  Action,
  getModule,
  Module,
  Mutation,
  VuexModule
} from 'vuex-module-decorators';

import ServicePointModel from '@/api/address/service-point.model';
import DeliveryRequestMessageModel from '@/api/order-request-message/order-request-message.model';
import OrderTrackingModel from '@/api/order-tracking/order-tracking.model';
import { orderTrackingService } from '@/api/order-tracking/order-tracking.service';
import RequestModel from '@/api/request/request.model';
import StatusModel from '@/api/status/status.model';
import TrackingModel from '@/api/tracking/tracking.model';
import { MqttPayloadModel } from '@/modules/main/models/mqtt-payload.model';
import { mainStore } from '@/modules/main/store';
import {
  ORDER_REQUEST_STATUS,
  ORDER_REQUEST_TYPES,
  TRACKING_STATUS
} from '@/modules/tracking/constants';
import store from '@/plugins/store.plugin';

/**
 * Store.
 */
@Module({
  dynamic: true,
  name: 'tracking',
  namespaced: true,
  store
})
export default class Tracking extends VuexModule {
  /**
   * Property `requestMessage`.
   */
  requestMessage: string = null;

  /**
   * Property `addressChanged`.
   */
  addressChanged: {
    additionalInformation?: string;
    city?: string;
    countryCode?: string;
    countryName?: string;
    doorNumber?: number;
    isForAutomaticApproval?: boolean;
    isValidated?: boolean;
    latitude?: number;
    longitude?: number;
    servicePointCode?: string;
    street?: string;
    zipCode?: string;
  } = null;

  /**
   * Property `servicePointChanged`.
   */
  servicePointChanged: ServicePointModel = null;

  /**
   * Property `dayChanged`.
   */
  dayChanged: string = null;

  /**
   * Property `chatMessages`.
   */
  chatMessages: DeliveryRequestMessageModel[] = [];

  /**
   * Property `servicePoints`.
   */
  servicePoints: ServicePointModel[] = [];

  /**
   * Property `trackingData`.
   */
  private trackingData: OrderTrackingModel = null;

  /**
   * Property `vehicleCoordinatesPayload`.
   */
  private vehicleCoordinatesPayload: MqttPayloadModel = null;

  /**
   * Getter `canCreateRequests`.
   */
  get canCreateRequests() {
    return this.trackingData?.canCreateRequests;
  }

  /**
   * Getter `canShowVehicle`.
   */
  get canShowVehicle() {
    return this.trackingData?.canShowVehicle;
  }

  /**
   * Getter `betweenFromDate`.
   */
  get betweenFromDate() {
    return this.trackingData?.betweenFromDate;
  }

  /**
   * Getter `betweenToDate`.
   */
  get betweenToDate() {
    return this.trackingData?.betweenToDate;
  }

  /**
   * Getter `collect`.
   */
  get collect() {
    return this.trackingData?.collect;
  }

  /**
   * Getter `delivery`.
   */
  get delivery() {
    return {
      ...this.trackingData?.delivery,
      numberPackages: this.trackingData?.numberPackages || 0
    };
  }

  /**
   * Getter `order`.
   */
  get order() {
    return this.isDelivery ? { ...this.delivery } : { ...this.collect };
  }

  /**
   * Getter `orderCode`.
   */
  get orderCode() {
    return this.isDelivery
      ? this.delivery?.orderCode
      : this.collect?.order?.code;
  }

  /**
   * Getter `deliverySurvey`.
   */
  get deliverySurvey() {
    return this.trackingData?.deliverySurvey;
  }

  /**
   * Getter `distributionCenter`.
   */
  get distributionCenter() {
    return this.trackingData?.distributionCenter;
  }

  /**
   * Getter `driver`.
   */
  get driver() {
    return this.trackingData?.driver;
  }

  /**
   * Getter `isDelivery`.
   */
  get isDelivery() {
    return this.orderType === 'DELIVERY';
  }

  /**
   * Getter `isCollect`.
   */
  get isCollect() {
    return this.orderType === 'COLLECT';
  }

  /**
   * Getter `numberPackages`.
   */
  get numberPackages() {
    return this.trackingData?.numberPackages;
  }

  /**
   * Getter `orderType`.
   */
  get orderType() {
    return this.trackingData?.orderType;
  }

  /**
   * Getter `requestPendingTreatment`.
   */
  get requestPendingTreatment() {
    return this.trackingData?.requestPendingTreatment;
  }

  /**
   * Getter `tracking`.
   */
  get tracking() {
    return this.trackingData?.tracking;
  }

  /**
   * Getter `trackingNumber`.
   */
  get trackingNumber() {
    return this.isDelivery
      ? this.delivery?.trackingNumber
      : this.collect?.order.trackingNumber;
  }

  /**
   * Getter `requests`.
   */
  get requests() {
    return this.trackingData?.requests;
  }

  /**
   * Getter `vehicle`.
   */
  get vehicle() {
    return this.trackingData?.vehicle;
  }

  /**
   * Getter `route`.
   */
  get route() {
    return this.trackingData?.route;
  }

  /**
   * Getter `servicePointOrderPin`.
   */
  get servicePointOrderPin() {
    return this.trackingData?.servicePointOrderPin;
  }

  /**
   * Getter `servicePointRetentionTime`.
   */
  get servicePointRetentionTime() {
    return this.trackingData?.servicePointRetentionTime || 0;
  }

  /**
   * Getter `vehicleCoordinates`.
   */
  get vehicleCoordinates() {
    const { message } = this.vehicleCoordinatesPayload || {};

    const { direction = null, latitude = null, longitude = null } =
      message || {};

    return { direction, latitude, longitude };
  }

  /**
   * Getter stored chat messages.
   */
  get storedChatMessages() {
    return [...this.chatMessages];
  }

  /**
   * Getter `orderProcessDelayed`.
   */
  get orderProcessDelayed() {
    return this.trackingData?.orderProcessDelayed;
  }

  /**
   * Mutation `addChatMessage`.
   */
  @Mutation
  addChatMessage(data: DeliveryRequestMessageModel) {
    this.chatMessages.push(data);
  }

  /**
   * Mutation `resetChatMessages`.
   */
  @Mutation
  resetChatMessages() {
    this.chatMessages = [];
  }

  /**
   * Mutation `setServicePoints`.
   */
  @Mutation
  setServicePoints(servicePoints: ServicePointModel[]) {
    this.servicePoints = servicePoints;
  }

  /**
   * Mutation `setTrackingData`.
   */
  @Mutation
  setTrackingData(data: OrderTrackingModel) {
    // Hide `NOT_DELIVERED` or `NOT_COLLECTED` tracking status setted from requests.
    // If current tracking `NOT_DELIVERED` or `NOT_COLLECTED` and is hidden,
    // add a hidden `PROCESSING` tracking status.
    const {
      requests: requestsHistory = null,
      tracking: trackingHistory = null
    } = data || {};

    // Build history.
    let history: {
      historyType: 'request' | 'tracking';
      request?: RequestModel;
      statusDate: string;
      tracking?: TrackingModel;
    }[] = [];

    trackingHistory.map(tracking => {
      const { statusDate = null } = tracking || {};

      history.push({
        historyType: 'tracking',
        statusDate: statusDate,
        tracking
      });
    });

    requestsHistory.map(request => {
      const { statusDate = null } = request || {};

      history.push({
        historyType: 'request',
        request,
        statusDate: statusDate
      });
    });

    history = orderBy(
      history,
      [item => (item?.statusDate ? new Date(item.statusDate).getTime() : 0)],
      ['desc']
    );

    // Divide history in parts, on every tracking status `PROCESSING`.
    const historyParts: {
      historyType: 'request' | 'tracking';
      request?: RequestModel;
      statusDate: string;
      tracking?: TrackingModel;
    }[][] = [];

    let previousIndex = 0;

    history.forEach((historyItem, index) => {
      const { historyType = null, tracking = null } = historyItem || {};
      const { status = null } = tracking || {};
      const { code = null } = status || {};

      if (historyType === 'tracking' && code === TRACKING_STATUS.PROCESSING) {
        historyParts.push(history.slice(previousIndex, index + 1));

        previousIndex = index + 1;
      } else if (index === history.length - 1) {
        historyParts.push(history.slice(previousIndex, index + 1));
      }
    });

    // Hide `NOT_DELIVERED` or `NOT_COLLECTED` tracking status setted from requests, on each part.
    historyParts.forEach((historyPart, historyPartIndex) => {
      const notDeliveredIndex = historyPart.findIndex(historyItem => {
        const { historyType = null, tracking = null } = historyItem || {};
        const { status = null } = tracking || {};
        const { code = null } = status || {};

        return (
          historyType === 'tracking' &&
          (code === TRACKING_STATUS.NOT_DELIVERED ||
            code === TRACKING_STATUS.NOT_COLLECTED)
        );
      });

      const requestIndex = historyPart.findIndex(historyItem => {
        const { historyType = null, request = null } = historyItem || {};
        const { status = null, type = null } = request || {};
        const { code = null } = status || {};

        const isChangeDate = [
          ORDER_REQUEST_TYPES.CHANGE_DATE,
          ORDER_REQUEST_TYPES.CHANGE_DATE_AND_ADDRESS
        ].includes(type);

        const isTreated = [
          ORDER_REQUEST_STATUS.AUTOMATIC_PENDING_TREATMENT,
          ORDER_REQUEST_STATUS.TREATED
        ].includes(code);

        return historyType === 'request' && isChangeDate && isTreated;
      });

      if (notDeliveredIndex !== -1 && requestIndex !== -1) {
        let historyItem = historyPart[notDeliveredIndex];
        const { tracking } = historyItem || {};

        historyItem = {
          ...historyItem,
          tracking: {
            ...tracking,
            hideHistory: true
          }
        };

        historyPart.splice(notDeliveredIndex, 1, historyItem);

        historyParts.splice(historyPartIndex, 1, historyPart);
      }
    });

    history = flatten(historyParts);

    const newTrackingHistory = history
      .filter(({ historyType }) => historyType === 'tracking')
      .map(({ tracking }) => tracking);

    const [currentTracking = null] = newTrackingHistory || [];
    const {
      hideHistory = false,
      status: currentTrackingStatus = null,
      statusDate: currentTrackingStatusDate = null
    } = currentTracking || {};
    const { code: currentTrackingStatusCode = null } =
      currentTrackingStatus || {};

    if (
      (currentTrackingStatusCode === TRACKING_STATUS.NOT_DELIVERED ||
        currentTrackingStatusCode === TRACKING_STATUS.NOT_COLLECTED) &&
      hideHistory
    ) {
      newTrackingHistory.unshift(
        new TrackingModel({
          hideHistory: true,
          status: new StatusModel({ code: TRACKING_STATUS.PROCESSING }),
          statusDate: currentTrackingStatusDate
        })
      );
    }

    // Set data.
    this.trackingData = { ...data, tracking: newTrackingHistory };

    const {
      collect = null,
      delivery = null,
      distributionCenter = null,
      orderProcessDelayed = false,
      orderType = null
    } = data || {};
    const { code = null } = distributionCenter || {};

    mainStore.setOrderType(orderType);
    mainStore.setOrderProcessDelayed(orderProcessDelayed);
    mainStore.setCollectData(collect);
    mainStore.setDeliveryData(delivery);
    mainStore.setDistributionCenterCode(code);
  }

  /**
   * Mutation `setRequestPendingTreatment`.
   */
  @Mutation
  setRequestPendingTreatment({ key = null, value = false } = {}) {
    if (key) {
      this.trackingData.requestPendingTreatment[key] = value;
    }
  }

  /**
   * Mutation `setRequestMessage`.
   */
  @Mutation
  setRequestMessage({ message = null } = {}) {
    this.requestMessage = message;
  }

  /**
   * Mutation `setChangeDeliveryAddress`.
   */
  @Mutation
  setChangeDeliveryAddress({ address }) {
    this.addressChanged = address;
  }

  /**
   * Mutation `setChangeServicePoint`.
   */
  @Mutation
  setChangeServicePoint(servicePoint) {
    this.servicePointChanged = servicePoint;
  }

  /**
   * Mutation `setChangeDeliveryDay`.
   */
  @Mutation
  setChangeDeliveryDay({ day = null } = {}) {
    this.dayChanged = day;
  }

  /**
   * Mutation `setVehicleCoordinatesMessage`.
   */
  @Mutation
  setVehicleCoordinatesPayload(payload: MqttPayloadModel) {
    const { messageDate: oldMessageDate = null } =
      this.vehicleCoordinatesPayload || {};

    const { messageDate: newMessageDate = null } = payload || {};

    if (
      !oldMessageDate ||
      !newMessageDate ||
      oldMessageDate <= newMessageDate
    ) {
      this.vehicleCoordinatesPayload = payload;
    }
  }

  /**
   * Action `getData`.
   */
  @Action({ commit: 'setTrackingData' })
  getData(key: string): Promise<OrderTrackingModel> {
    return orderTrackingService.getById(key);
  }
}

/**
 * Export `trackingStore`.
 */
export const trackingStore = getModule(Tracking);
